import { useState, useEffect } from 'react';
import { Star, Check, X as XIcon, Lock, Target, Award, Calendar } from 'lucide-react';
import axios from 'axios';
import TeamLogo from './TeamLogo';
import { shortTeamName } from './teamUtils';
import { roundLabel } from '../utils/wcLabels';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function SportBadge({ sport }) {
  const cls =
    sport === 'AFL' ? 'tipster-badge-afl'
    : sport === 'NRL' ? 'tipster-badge-nrl'
    : 'tipster-badge-wc';
  const label = sport === 'WC' ? 'World Cup' : sport;
  return <span className={cls}>{label}</span>;
}

function formatDeadline(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-AU', {
    weekday: 'short', day: 'numeric', month: 'short',
    hour: 'numeric', minute: '2-digit', hour12: true,
    timeZone: 'Australia/Sydney',
  }) + ' AEST';
}

// `match_time` is stored as naive AEST wall clock (no TZ). Formatting it via
// `new Date(...).toLocaleString({ timeZone: 'Australia/Sydney' })` would treat
// the string as UTC and shift it wrong. We parse the raw components and format
// directly so every browser (regardless of local TZ) shows the same AEST time.
const _MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const _DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
function formatMatchAEST(dateStr) {
  if (!dateStr) return '';
  const m = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
  if (!m) return '';
  const [, Y, Mo, D, H, Mi] = m;
  const d = new Date(Number(Y), Number(Mo) - 1, Number(D));
  const weekday = _DAYS[d.getDay()];
  const monShort = _MONTHS[Number(Mo) - 1];
  const hr24 = Number(H);
  const ampm = hr24 >= 12 ? 'pm' : 'am';
  const hr12 = hr24 % 12 === 0 ? 12 : hr24 % 12;
  return `${weekday}, ${Number(D)} ${monShort}, ${hr12}:${Mi} ${ampm} AEST`;
}

function TipCell({ tip, fixture, logoUrl }) {
  if (!tip) {
    return <div className="flex items-center justify-center text-gray-300 text-xs">—</div>;
  }
  if (tip.hidden) {
    return (
      <div className="rounded-sm px-2 py-1.5 flex items-center justify-center bg-gray-100/70" data-testid="tip-cell-hidden">
        <Lock className="w-3.5 h-3.5 text-gray-400" />
      </div>
    );
  }
  const isCorrect = tip.is_correct === true;
  const isWrong = tip.is_correct === false;
  const pending = !fixture.is_completed;

  const bgClass = pending
    ? 'bg-gray-50'
    : isCorrect ? 'bg-green-50 ring-1 ring-green-200'
    : 'bg-red-50 ring-1 ring-red-200';
  const txtClass = pending ? 'text-gray-700' : isCorrect ? 'text-green-700' : 'text-red-700';

  return (
    <div className={`rounded-sm px-2 py-1.5 flex items-center gap-1.5 ${bgClass}`}>
      <TeamLogo teamName={tip.predicted_winner} logoUrl={logoUrl} size={20} />
      <div className="flex-1 min-w-0">
        <div className={`text-xs font-semibold truncate ${txtClass}`}>
          {shortTeamName(tip.predicted_winner)}
        </div>
        {(tip.margin_prediction != null || tip.goalkicker_prediction || tip.home_score_prediction != null) && (
          <div className="text-[10px] text-gray-500 truncate flex items-center gap-1">
            {tip.home_score_prediction != null && tip.away_score_prediction != null && (
              <span>{tip.home_score_prediction}–{tip.away_score_prediction}</span>
            )}
            {tip.margin_prediction != null && <span>m:{tip.margin_prediction}</span>}
            {tip.goalkicker_prediction && (
              <span>gk:{tip.goalkicker_prediction.split(' ')[0]}</span>
            )}
          </div>
        )}
      </div>
      {!pending && (
        <span className={`text-xs font-bold ${tip.points_earned > 0 ? 'text-[#007AFF]' : 'text-gray-400'}`}>
          {tip.points_earned > 0 ? `+${Math.round(tip.points_earned * 100) / 100}` : '0'}
        </span>
      )}
      {pending && tip.potential_points != null && (
        <span
          className="text-[10px] font-bold text-[#37003C] bg-[#37003C]/10 rounded-sm px-1 py-0.5 flex-shrink-0"
          title={`Potential points if ${tip.predicted_winner} wins`}
          data-testid="odds-potential-points"
        >
          {(Math.round(tip.potential_points * 100) / 100).toFixed(2)}pt
        </span>
      )}
      {isCorrect && !pending && <Check className="w-3 h-3 text-green-600 flex-shrink-0" strokeWidth={3} />}
      {isWrong && !pending && <XIcon className="w-3 h-3 text-red-600 flex-shrink-0" strokeWidth={3} />}
    </div>
  );
}

function RoundMatrix({ round, users, currentUserId, wcLogos, eplLogos, alLogos }) {
  const userColWidth = '9.5rem';
  const matchCol = '20rem';
  const gridTemplate = `${matchCol} repeat(${users.length}, minmax(${userColWidth}, 1fr))`;
  const sportLogos = round.sport === 'WC' ? wcLogos : round.sport === 'EPL' ? eplLogos : round.sport === 'AL' ? alLogos : {};
  const useSportLogos = round.sport === 'WC' || round.sport === 'EPL' || round.sport === 'AL';

  return (
    <div className="tipster-card p-0 overflow-hidden" data-testid={`current-tips-${round.sport}`}>
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3 px-5 py-4 border-b border-gray-100">
        <SportBadge sport={round.sport} />
        <div className="font-bold text-[#1A1A1A]">
          {roundLabel(round.sport, round.round_number)}
        </div>
        {!round.tips_revealed ? (
          <span className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-600 text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-sm" data-testid={`tips-locked-${round.sport}`}>
            <Lock className="w-3 h-3" />
            Tips locked
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 bg-green-100 text-green-700 text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-sm">
            <Check className="w-3 h-3" strokeWidth={3} />
            Tips Revealed
          </span>
        )}
        <div className="ml-auto flex items-center gap-1.5 text-xs text-gray-500">
          <Calendar className="w-3.5 h-3.5" />
          <span>{round.tips_revealed ? 'Revealed' : 'Reveal at'} {formatDeadline(round.deadline)}</span>
        </div>
      </div>

      {/* Matrix */}
      <div className="overflow-x-auto">
        <div className="min-w-fit">
          {/* Column header */}
          <div className="grid gap-2 px-4 py-3 bg-gray-50 border-b border-gray-200" style={{ gridTemplateColumns: gridTemplate }}>
            <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Match</div>
            {users.map(u => {
              const totals = round.user_totals?.[u.user_id];
              return (
                <div key={u.user_id} className="text-center">
                  <div className={`text-xs font-bold uppercase tracking-wider truncate ${u.user_id === currentUserId ? 'text-[#007AFF]' : 'text-[#1A1A1A]'}`} title={u.name}>
                    {u.name.split(' ')[0]}
                  </div>
                  {totals ? (
                    <div className="text-[11px] text-gray-500 mt-0.5">
                      <span className="text-[#007AFF] font-bold">{Math.round((totals.points || 0) * 100) / 100}</span>
                      <span className="text-gray-400"> · {totals.correct}/{totals.tips}</span>
                    </div>
                  ) : (
                    <div className="text-[11px] text-gray-400 mt-0.5">—</div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Match rows */}
          {round.matches.map((m) => (
            <div
              key={m.fixture_id}
              className={`grid gap-2 px-4 py-3 border-b border-gray-50 last:border-b-0 ${m.is_game_of_round ? 'bg-yellow-50/30' : ''}`}
              style={{ gridTemplateColumns: gridTemplate }}
              data-testid={`current-match-${m.fixture_id}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                {m.is_game_of_round && <Star className="w-4 h-4 text-[#FFCC00] fill-current flex-shrink-0" title="Game of the Round" />}
                <div className="flex flex-col min-w-0 flex-1">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <TeamLogo teamName={m.home_team} logoUrl={useSportLogos ? sportLogos?.[m.home_team] : undefined} size={22} />
                      <span className="text-sm font-semibold text-[#1A1A1A] truncate">{shortTeamName(m.home_team)}</span>
                    </div>
                    <span className="text-xs text-gray-400 font-mono mx-1">v</span>
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span className="text-sm font-semibold text-[#1A1A1A] truncate">{shortTeamName(m.away_team)}</span>
                      <TeamLogo teamName={m.away_team} logoUrl={useSportLogos ? sportLogos?.[m.away_team] : undefined} size={22} />
                    </div>
                    {m.is_completed && (
                      <span className="text-xs font-mono text-gray-500 ml-auto flex-shrink-0">{m.home_score}–{m.away_score}</span>
                    )}
                  </div>
                  {m.match_time && (
                    <div className="text-[10.5px] text-gray-400 mt-0.5" data-testid={`match-kickoff-${m.fixture_id}`}>
                      {formatMatchAEST(m.match_time)}
                    </div>
                  )}
                </div>
              </div>
              {users.map(u => (
                <TipCell
                  key={u.user_id}
                  tip={m.tips_by_user[u.user_id]}
                  fixture={m}
                  logoUrl={useSportLogos && m.tips_by_user[u.user_id] ? sportLogos?.[m.tips_by_user[u.user_id].predicted_winner] : undefined}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function CurrentWeekTips({ currentUserId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wcLogos, setWcLogos] = useState({});
  const [eplLogos, setEplLogos] = useState({});
  const [alLogos, setAlLogos] = useState({});

  useEffect(() => {
    let mounted = true;
    axios.get(`${API_URL}/api/dashboard/current-week-tips`, { withCredentials: true })
      .then(res => mounted && setData(res.data))
      .catch(() => mounted && setData(null))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  // Fetch WC + EPL logo maps once (WC = flags keyed by nation, EPL = club crests keyed by team).
  // AFL/NRL are handled by UPLOADED_LOGOS inside TeamLogo already.
  useEffect(() => {
    let mounted = true;
    axios.get(`${API_URL}/api/teams/WC/logos`)
      .then(res => mounted && setWcLogos(res.data || {}))
      .catch(() => mounted && setWcLogos({}));
    axios.get(`${API_URL}/api/teams/EPL/logos`)
      .then(res => mounted && setEplLogos(res.data || {}))
      .catch(() => mounted && setEplLogos({}));
    axios.get(`${API_URL}/api/teams/AL/logos`)
      .then(res => mounted && setAlLogos(res.data || {}))
      .catch(() => mounted && setAlLogos({}));
    return () => { mounted = false; };
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-8 h-8 border-2 border-[#007AFF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data || !data.rounds || data.rounds.length === 0) return null;

  // Hide the closed WC competition from the Current Week Tips widget.
  const activeRounds = data.rounds.filter((r) => r.sport !== 'WC');
  if (activeRounds.length === 0) return null;

  return (
    <section data-testid="current-week-tips-section">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
        <h2 className="text-2xl font-bold uppercase tracking-tight">Current Week Tips</h2>
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <Award className="w-4 h-4 text-[#FFCC00]" />
          <span>Tips stay private until Thursday 5:05pm AEST</span>
        </div>
      </div>
      <div className="space-y-6">
        {activeRounds.map((r) => (
          <RoundMatrix
            key={r.sport}
            round={r}
            users={data.users}
            currentUserId={currentUserId}
            wcLogos={wcLogos}
            eplLogos={eplLogos}
            alLogos={alLogos}
          />
        ))}
      </div>
    </section>
  );
}
